import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import cn from 'classnames';

import {
  ExportButton,
  PaginationGroup,
  LimitPaginationButton,
  EmptyState,
  FilterButton,
  ClearButton,
  SelectDropDown,
  DatePicker,
  TableTitle,
  Loading
} from '../../common/Elements/Elements';
import InputSearch from '../../common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import FeaturesAdoptionHeader from './parts/FeaturesAdoptionHeader/FeaturesAdoptionHeader';
import FeaturesAdoptionTable from './parts/FeaturesAdoptionTable/FeaturesAdoptionTable';
import Widgets from './parts/Widgets/Widgets';
import Tags from './parts/Tags/Tags';
import ChartBox from './parts/ChartBox/ChartBox';

import { getRequest, postRequest } from 'utils/requests';
import { validationNumber } from 'utils/validation';
import { featuresReq, createSaveList } from './helpers/helpers';
import {
  parsQueryForSave,
  createFilterCountry,
  createFilterSize
} from 'utils/common';
import { initialTableShow } from 'utils/settings';
import { exportConfig } from './helpers/configurates';

import classes from './FeaturesAdoption.module.scss';

const FeaturesAdoption = () => {
  const { t } = useTranslation(['activity']);

  const [filters, setFilters] = useState({
    filterInput: '',
    size: [],
    plan: [],
    date: { startDate: null, endDate: null },
    billingCycle: [],
    country: [],
    industry: [],
    accountManager: [],
    customerSuccessEngineer: [],
    fa: [],
    orAnd: 'OR',
    fromTo: { arrFrom: '', arrTo: '' }
  });

  const [tableState, setTableState] = useState({
    fieldName: 'arr',
    direction: initialTableShow.direction,
    currentPage: initialTableShow.currentPage,
    itemsPerPage: initialTableShow.itemsPerPage
  });

  const [uiState, setUiState] = useState({
    filterBtn: false,
    loader: true,
    loaderTable: false,
    dataConection: false,
    trigerResetDatePicker: false
  });

  const [faData, setFaData] = useState({});
  const [faFilters, setFaFilters] = useState({});

  const numberОfFilters = 6;
  const history = useHistory();
  const location = useLocation();

  let faCountry = [];
  let companySize = [];

  const resetCurrentPage = () => {
    setTableState(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (key, value) => {
    resetCurrentPage();
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filters.filterInput, value =>
    handleFilterChange('filterInput', value)
  );

  const handleFromTo = e => {
    resetCurrentPage();
    if (e.currentTarget.id) {
      setFilters(prev => ({
        ...prev,
        fromTo: {
          ...prev.fromTo,
          [`${e.currentTarget.id}From`]: '',
          [`${e.currentTarget.id}To`]: ''
        }
      }));
    } else {
      if (!validationNumber(e.target.value)) {
        setFilters(prev => ({
          ...prev,
          fromTo: {
            ...prev.fromTo,
            [e.target.name]: e.target.value
          }
        }));
      }
    }
  };

  const handleFilterBtn = () => {
    setUiState(prev => ({ ...prev, filterBtn: !prev.filterBtn }));
  };

  const handleLimitChange = limit => {
    if (tableState.itemsPerPage === limit) {
      return;
    }
    resetCurrentPage();
    setTableState(prev => ({ ...prev, itemsPerPage: limit }));
  };

  const handleSortable = field => () => {
    if (field === tableState.fieldName) {
      const directionTemp = tableState.direction * -1;
      setTableState(prev => ({ ...prev, direction: directionTemp }));
    } else {
      setTableState(prev => ({ ...prev, fieldName: field }));
    }
  };

  const closeTag = item => () => {
    const result = filters.fa.filter(word => word !== item);
    setFilters(prev => ({ ...prev, fa: result }));
  };

  const onClearSearch = () => {
    setFilters(prev => ({ ...prev, filterInput: '' }));
  };

  const resetAllfilters = () => {
    setFilters({
      filterInput: '',
      size: [],
      plan: [],
      date: { startDate: null, endDate: null },
      billingCycle: [],
      country: [],
      industry: [],
      accountManager: [],
      customerSuccessEngineer: [],
      fa: [],
      orAnd: 'OR',
      fromTo: { arrFrom: '', arrTo: '' }
    });
    setTableState(prev => ({
      ...prev,
      direction: initialTableShow.direction,
      itemsPerPage: initialTableShow.itemsPerPage,
      currentPage: initialTableShow.currentPage
    }));
    setUiState(prev => ({
      ...prev,
      trigerResetDatePicker: !prev.trigerResetDatePicker
    }));
  };

  const replaceHistory = () => {
    const {
      filterInput,
      size,
      plan,
      date,
      billingCycle,
      country,
      industry,
      accountManager,
      customerSuccessEngineer,
      fa,
      fromTo,
      orAnd
    } = filters;
    const { direction, itemsPerPage, currentPage, fieldName } = tableState;

    const query = `name=${filterInput}&size=${size}&pricing=${plan}&start=${
      date.startDate
    }&end=${
      date.endDate
    }&fa=${fa}&cycle=${billingCycle}&country=${country}&accountManager=${accountManager}&customerSuccessEngineer=${customerSuccessEngineer}&industry=${parsQueryForSave(
      industry
    )}&fromTo=${JSON.stringify(
      fromTo
    )}&direction=${direction}&limit=${itemsPerPage}&page=${currentPage}&fieldName=${fieldName}&orAnd=${orAnd}`;
    history.replace(`/product/features-adoption?${query}`);
  };

  useEffect(() => {
    let cancelled = false;
    getRequest('/product/fa/filters')
      .then(res => {
        if (!cancelled) {
          setFaFilters(res.body);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Error fetching filters:', err);
        }
      });
    return () => (cancelled = true);
  }, []);

  useEffect(() => {
    if (location.search) {
      const loadList = createSaveList(location);
      setFilters({
        filterInput: loadList.name,
        size: loadList.size,
        plan: loadList.pricing,
        date: loadList.date,
        billingCycle: loadList.cycle,
        country: loadList.country,
        industry: loadList.industry,
        accountManager: loadList.accountManager,
        customerSuccessEngineer: loadList.customerSuccessEngineer,
        fa: loadList.fa,
        orAnd: loadList.orAnd,
        fromTo: loadList.fromTo
      });
      setTableState({
        direction: loadList.direction,
        itemsPerPage: loadList.limit,
        currentPage: loadList.page,
        fieldName: loadList.fieldName
      });
    } else {
      setFilters(prev => ({ ...prev, fromTo: { arrFrom: '', arrTo: '' } }));
      history.replace(`/product/features-adoption`);
      resetAllfilters();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    replaceHistory();
    // eslint-disable-next-line
  }, [filters, tableState]);

  useEffect(() => {
    if (faFilters.country && faFilters.companySize) {
      createFilterCountry(faFilters.country, faCountry);
      createFilterSize(faFilters.companySize, companySize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faFilters]);

  useEffect(() => {
    let cancelled = false;
    setUiState(prev => ({ ...prev, loaderTable: true }));

    const { currentPage, itemsPerPage, fieldName, direction } = tableState;
    const {
      date,
      filterInput,
      size,
      fa,
      billingCycle,
      plan,
      country,
      industry,
      accountManager,
      customerSuccessEngineer,
      fromTo,
      orAnd
    } = filters;
    const linkForRequest = `/product/fa/list?page=${currentPage}&limit=${itemsPerPage}`;
    const REQ_BODY = featuresReq(
      date,
      filterInput,
      size,
      fa,
      billingCycle,
      plan,
      country,
      industry,
      accountManager,
      customerSuccessEngineer,
      fromTo,
      fieldName,
      direction,
      orAnd
    );

    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        setFaData(res.body);
        setUiState(prev => ({
          ...prev,
          dataConection: true,
          loader: false,
          loaderTable: false
        }));
      })
      .catch(err => {
        if (!cancelled) {
          setUiState(prev => ({
            ...prev,
            dataConection: false,
            loader: false,
            loaderTable: false
          }));
          console.error('Error fetching data:', err);
        }
      });
    return () => (cancelled = true);
    // eslint-disable-next-line
  }, [filters, tableState]);

  if (uiState.loader) {
    return <Loading />;
  }

  if (!uiState.dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <FeaturesAdoptionHeader
        filterData={faFilters.featureAdoption}
        input={{
          value: filters.fa,
          onChange: value => handleFilterChange('fa', value)
        }}
        radio={{
          value: filters.orAnd,
          onChange: e => handleFilterChange('orAnd', e.target.value)
        }}
        fa={faFilters.fa}
      />
      <Tags features={filters.fa} close={closeTag} />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.COMPANY_NAME_PLACEHOLDER')}
          value={filters.filterInput}
          onChange={e => handleFilterChange('filterInput', e.target.value)}
          onClearSearch={onClearSearch}
          label={t('activity:filters.COMPANY_NAME')}
          disabled={uiState.loaderTable}
        />
        <FilterButton active={uiState.filterBtn} onClick={handleFilterBtn} />
      </div>
      <div
        className={cn(
          uiState.filterBtn ? classes.filters : classes.filters_close
        )}
      >
        <SelectDropDown
          label={t('activity:filters.COMPANY_SIZE')}
          options={companySize}
          input={{
            value: filters.size,
            onChange: value => handleFilterChange('size', value)
          }}
          multi
          renderKey={'label'}
        />
        <DatePicker
          label={t('activity:filters.FIRST_PAYMENT')}
          input={{
            value: filters.date,
            onChange: value => handleFilterChange('date', value)
          }}
          trigerResetDatePicker={uiState.trigerResetDatePicker}
        />
        <SelectDropDown
          label={t('activity:filters.PRICING_PLAN')}
          options={faFilters.plan}
          input={{
            value: filters.plan,
            onChange: value => handleFilterChange('plan', value)
          }}
          multi
        />
        <SelectDropDown
          label={t('activity:filters.BILLING_CYCLE')}
          options={faFilters.billingCycle}
          input={{
            value: filters.billingCycle,
            onChange: value => handleFilterChange('billingCycle', value)
          }}
          multi
        />
        <SelectDropDown
          searchable={true}
          options={faCountry}
          input={{
            value: filters.country,
            onChange: value => handleFilterChange('country', value)
          }}
          label={t('activity:filters.COUNTRY')}
          multi
          searchKeys={['value', 'findName']}
          renderKey={'fullName'}
          checkRenderKey={'fullName'}
        />
        <SelectDropDown
          label={t('activity:filters.INDUSTRY')}
          options={faFilters.industry}
          input={{
            value: filters.industry,
            onChange: value => handleFilterChange('industry', value)
          }}
          multi
        />
        <SelectDropDown
          label={t('activity:filters.ACCOUNT_MANAGER_')}
          options={faFilters.accountManager}
          input={{
            value: filters.accountManager,
            onChange: value => handleFilterChange('accountManager', value)
          }}
          multi
        />
        <SelectDropDown
          label={t('activity:about.CS_ENGINEER')}
          options={faFilters.customerSuccessEngineer}
          input={{
            value: filters.customerSuccessEngineer,
            onChange: value =>
              handleFilterChange('customerSuccessEngineer', value)
          }}
          multi
        />
        <ClearButton
          onClick={resetAllfilters}
          numberОfFilters={numberОfFilters}
        />
      </div>
      <Widgets wData={faData?.headerData} />
      <ChartBox features={filters.fa} />
      <div className={classes.actions}>
        <TableTitle
          title={t('activity:features.FEATURES_ADOPTION')}
          additional={faData.totalRecords}
        />
        <div className={classes.actions_right}>
          <ExportButton
            exportConfig={exportConfig}
            isDisabled={!faData.totalRecords}
          />
          <LimitPaginationButton
            currentLimit={tableState.itemsPerPage}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      <FeaturesAdoptionTable
        tableData={faData.data}
        handleSortable={handleSortable}
        direction={tableState.direction}
        fieldName={tableState.fieldName}
        fromTo={filters.fromTo}
        handleFromTo={handleFromTo}
        loader={uiState.loaderTable}
      />
      {faData.data &&
        uiState.loaderTable === false &&
        faData.totalPages > 1 && (
          <PaginationGroup
            pageCount={faData.totalPages}
            setPage={page =>
              setTableState(prev => ({ ...prev, currentPage: page }))
            }
            currentPage={tableState.currentPage}
            itemsTotal={faData.totalRecords}
            itemsPerPage={tableState.itemsPerPage}
          />
        )}
    </>
  );
};

export default FeaturesAdoption;
